'use client';

import classNames from 'classnames';
import { CSSProperties, useEffect, useState } from 'react';

// Brand palette mirrored from GoalMountainGrapic.tsx (the illustration these particles echo).
// Kept as literals on purpose: these are runtime values for the `--c` custom property, not
// Tailwind className tokens — and #625fdb matches the SVG's dots but has no config token.
const PARTICLE_COLORS = ['#9d90e1', '#9d90e1', '#e5e5ea', '#ffbb53', '#625fdb'];
export const PARTICLE_COUNT = 22;
const RISE_PX = 470;

interface Particle {
  x: string;
  sz: string;
  dur: string;
  delay: string;
  sway: string;
  rise: string;
  maxop: string;
  c: string;
}

function buildParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    x: `${(Math.random() * 100).toFixed(1)}%`,
    sz: `${(2.5 + Math.random() * 4).toFixed(1)}px`,
    dur: `${(10 + Math.random() * 12).toFixed(1)}s`,
    delay: `${(-Math.random() * 22).toFixed(1)}s`,
    sway: `${((Math.random() - 0.5) * 38).toFixed(0)}px`,
    rise: `-${(RISE_PX + Math.random() * 60).toFixed(0)}px`,
    maxop: (0.22 + Math.random() * 0.33).toFixed(2),
    c: PARTICLE_COLORS[i % PARTICLE_COLORS.length]
  }));
}

function particleStyle(p: Particle): CSSProperties {
  // Computed keys avoid TS excess-property checks for CSS custom properties.
  return {
    ['--x']: p.x,
    ['--sz']: p.sz,
    ['--dur']: p.dur,
    ['--delay']: p.delay,
    ['--sway']: p.sway,
    ['--rise']: p.rise,
    ['--maxop']: p.maxop,
    ['--c']: p.c
  } as CSSProperties;
}

export default function GoalEmptyStateAmbience({ active = true }: { active?: boolean }) {
  // Particles use Math.random() -> build client-side only to avoid SSR hydration mismatch.
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setParticles(buildParticles());
    }
  }, []);

  return (
    <div className={classNames('goal-ambience', !active && 'is-paused')} aria-hidden='true'>
      <div className='glow' />
      {particles.map((p, i) => (
        <span key={i} className='particle' style={particleStyle(p)} />
      ))}
    </div>
  );
}
